import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { autorun, runInAction, toJS } from "mobx";
import { getSearchMeta } from "./searchParamDecorator";

/**
 * One-way search param projection: MobX store -> URL.
 *
 * Goals:
 *  - Hydrate store once on initial mount (URL wins only at boot)
 *  - After hydration, treat MobX as the single source of truth
 *  - Project (serialize) decorated fields into the URL
 *  - Preserve state on full page refresh (values serialized into search params)
 *  - Do NOT listen to subsequent URL changes (manual mid-session edits only take effect after full reload)
 *  - Avoid history spam: default is replaceState, not pushState
 *
 * Differences vs useSearchParamSync:
 *  - No bi-directional syncing, no guard flags
 *  - Simpler mental model (single authority)
 *  - Faster: fewer effect restarts & no shallow diffing from URL side
 */
export interface ProjectionOptions {
  /** Remove params whose value equals default (default true). */
  elideDefaults?: boolean;
  /** Use history.replaceState instead of pushState (default true). */
  replaceHistory?: boolean;
  /** Debounce projection writes (ms). */
  debounceMs?: number;
  /** Optional base route; if provided and current path differs, navigate there once before projecting. */
  navigateToBase?: boolean;
  /** If true, only project while on (or under) the baseRoute; otherwise still project. Default false. */
  restrictToBase?: boolean;
  /** Optional debug logger (messages, ...args). */
  debug?: (msg: string, ...args: any[]) => void;
  /** Callback fired after one-time hydration completes. */
  onHydrated?: () => void;
}

/** Symbol attached to the store once hydration finished (value: true). */
export const SEARCH_PARAMS_HYDRATED = Symbol('SEARCH_PARAMS_HYDRATED');

export function useSearchParamProjection<T extends object>(
  store: T,
  ctor: Function,
  opts?: ProjectionOptions
) {
  const {
    elideDefaults = true,
    replaceHistory = true,
    debounceMs = 0,
    navigateToBase = false,
    restrictToBase = false,
    debug,
    onHydrated,
  } = opts || {};

  const meta = getSearchMeta(ctor);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const hydratedRef = useRef(false);
  const timerRef = useRef<number | undefined>();

  // Optional navigation enforcement BEFORE hydration.
  useEffect(() => {
    if (!navigateToBase || !meta.baseRoute) return;
    if (!location.pathname.startsWith(meta.baseRoute)) {
      debug?.("[projection] navigating to baseRoute", meta.baseRoute, "from", location.pathname);
      navigate({ pathname: meta.baseRoute, search: location.search }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateToBase, meta.baseRoute]);

  // One-time hydration on first mount. URL -> store ONLY ONCE.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true; // ensure we only hydrate once even if something re-renders

    const patch: Record<string | symbol, any> = {};
    let changed = false;
    meta.fields.forEach((spec, field) => {
      const raw = searchParams.get(spec.key);
      const parsed = spec.parse(raw);
      const value = parsed !== undefined ? parsed : spec.default;
      const current = (store as any)[field];
      // If the URL provides no value (raw === null) and the store already holds a non-default value
      // (i.e. something user code intentionally set BEFORE navigation/mount), we skip hydration for that field
      // to prevent clobbering an optimistic navigation update.
      if (raw === null && !shallowEqual(current, spec.default)) {
        debug?.("[projection] skip hydration for pre-populated field", field, {
          current,
          default: spec.default,
        });
        return;
      }
      if (!shallowEqual(current, value)) {
        patch[field as string] = value;
        changed = true;
      }
    });
    if (changed) {
      debug?.("[projection] hydrating store from URL", patch);
      runInAction(() => {
        for (const k of Object.keys(patch)) {
          const spec = meta.fields.get(k)!;
            const setter = spec?.setter && (store as any)[spec.setter];
            if (typeof setter === "function") setter(patch[k]);
            else (store as any)[k] = patch[k];
        }
      });
    } else {
      debug?.("[projection] no hydration changes needed");
    }
    // Mark store as hydrated so navigation helpers can detect it.
    try {
      (store as any)[SEARCH_PARAMS_HYDRATED] = true;
    } catch { /* non-fatal */ }
    onHydrated?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store -> URL projection after hydration.
  useEffect(() => {
    const dispose = autorun(() => {
      if (!hydratedRef.current) return; // don't project before hydration

      const onBase = !restrictToBase || !meta.baseRoute || location.pathname.startsWith(meta.baseRoute);
      if (!onBase) {
        debug?.("[projection] restrictToBase prevents projection on", location.pathname);
        return;
      }

      // If there are no decorated fields on the target store, projecting would wipe
      // unrelated search params from the URL (because we'd serialize an empty set).
      // Skip projection entirely in that case to avoid destructive clearing and loops
      // with other projection hooks continuously re-adding their params.
      if (meta.fields.size === 0) {
        debug?.("[projection] skipping URL update: no decorated fields found");
        return;
      }

      const nextParams = new URLSearchParams();
      meta.fields.forEach((spec, field) => {
        const rawValue = (store as any)[field];
        const snapshot = toJS(rawValue);
        const serialized = spec.serialize(snapshot);
        const isDefault = shallowEqual(snapshot, spec.default);
        const include = serialized !== undefined && (!elideDefaults || !isDefault);
        if (include) nextParams.set(spec.key, serialized!);
      });

      const currentString = window.location.search.startsWith('?') ? window.location.search.slice(1) : window.location.search;
      const nextString = nextParams.toString();
      if (currentString === nextString) {
        debug?.("[projection] URL already up-to-date");
        return;
      }

      debug?.("[projection] scheduling URL update", { currentString, nextString });

      const apply = () => {
        debug?.("[projection] applying URL update", nextString);
        setSearchParams(nextParams, { replace: replaceHistory });
      };

      if (debounceMs > 0) {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        // @ts-ignore
        timerRef.current = window.setTimeout(apply, debounceMs);
      } else {
        apply();
      }
    });

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      dispose();
    };
    // Intentionally NOT depending on searchParams or store object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.fields, elideDefaults, replaceHistory, debounceMs, restrictToBase, meta.baseRoute, location.pathname, setSearchParams]);
}

// Minimal shallow equality reused from sync version (could be imported if made shared).
const shallowEqual = (a: any, b: any): boolean => {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === "object") {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return false;
      return true;
    }
    const ak = Object.keys(a), bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    for (const k of ak) if (!Object.is(a[k], b[k])) return false;
    return true;
  }
  return false;
};

/**
 * Usage Example:
 *
 *  @searchRoute('/patients')
 *  class PatientSearchStore {
 *    @isSearchParam() query = '';
 *    @isSearchParam({ parse: intCodec.parse, serialize: intCodec.serialize, default: 0 }) page = 0;
 *  }
 *
 *  const store = useLocalObservable(() => new PatientSearchStore());
 *  useSearchParamProjection(store, PatientSearchStore, { debug: console.debug });
 */
